import { Loading, Notify, Report } from 'notiflix';
import { useNavigate } from 'react-router-dom';
import { forgotPassword as forgotPasswordReq, getUser, loginUser, logoutUser, registerUser } from '../data/api/auth/AuthRoutes';
import useIndex from './useIndex';
import validErrors from './validErrors';

const useAuth = () => {
    const navigate = useNavigate();
    const { setAuth, setLoading, token, setToken } = useIndex();

    // Login
    const login = async (data) => {
        setLoading(true);
        Loading.hourglass();
        try {
            const response = await loginUser(data);

            if (data.session) {
                localStorage.setItem('AUTH_TOKEN', response.data.token);
                sessionStorage.removeItem('AUTH_TOKEN');
            } else {
                sessionStorage.setItem('AUTH_TOKEN', response.data.token);
                localStorage.removeItem('AUTH_TOKEN');
            }
            setToken(response.data.token);
            Notify.success('Usuario ingresado correctamente');

            const authRes = await getUserAuth(response.data.token);
            if (authRes) {
                navigate('/ganacol/dashboard');
            }
        } catch (error) {
            // Caso especial: usuario deshabilitado para acceder al sistema.
            // El backend lo señala con code='USER_DISABLED'; mostramos un
            // Report con link a la página de contacto en vez del Notify
            // genérico (que es lo que hace validErrors). Usamos `failure`
            // (rojo / error) en lugar de `warning` para reflejar que la
            // cuenta está cancelada / no activa.
            if (error?.response?.data?.code === 'USER_DISABLED') {
                Report.failure(
                    'Cuenta no activa',
                    'No tienes una cuenta activa. Si deseas acceder, por favor contáctate con el administrador del sistema.',
                    'Contactar',
                    () => navigate('/contact'),
                    { backOverlayColor: 'rgba(0,0,0,0.5)' }
                );
            } else {
                validErrors(error);
            }
            setAuth({});
        } finally {
            Loading.remove();
            setLoading(false);
        }
    }

    // Register
    const register = async (data) => {
        setLoading(true);
        Loading.hourglass();
        try {
            const response = await registerUser(data);
            localStorage.setItem('AUTH_TOKEN', response.data.token);
            setToken(response.data.token);
            Report.success('Registro exitoso', 'El usuario ha sido registrado con exito', 'Ok', () => getUserAuth(response.data.token));
            navigate('/ganacol/dashboard');
        } catch (error) {
            validErrors(error);
        } finally {
            Loading.remove();
            setLoading(false);
        }
    }

    // Forgot Password
    const forgotPassword = async (email) => {
        setLoading(true);
        Loading.hourglass();
        try {
            const { data } = await forgotPasswordReq({ email });

            if (data?.success) {
                Notify.success('Se ha enviado a tu correo un link para cambiar tu contraseña.');
            }
            return data;
        } catch (error) {
            validErrors(error);
            return undefined;
        } finally {
            Loading.remove();
            setLoading(false);
        }
    }

    // Logout
    const logout = async () => {
        setLoading(true);

        // Limpiamos el storage ANTES de pegarle al endpoint /api/logout
        // por dos motivos:
        //  1. El interceptor 401 del cliente axios mira el storage para
        //     decidir si dispara el flujo de "sesión expirada"; si el
        //     token ya estaba revocado (p. ej. tras un self-disable) el
        //     /api/logout responde 401 y querríamos evitar ese popup.
        //  2. Aunque la llamada al servidor falle, el efecto deseado del
        //     usuario (cerrar sesión localmente) ya se logró.
        const tokenToInvalidate = token;
        localStorage.removeItem('AUTH_TOKEN');
        sessionStorage.removeItem('AUTH_TOKEN');
        setToken(null);
        setAuth(undefined);

        try {
            if (tokenToInvalidate) {
                // Best-effort: si el token ya está revocado o la red
                // falla, el cierre local sigue siendo válido. Por eso
                // capturamos el error y lo descartamos en silencio.
                await logoutUser(tokenToInvalidate).catch(() => { });
            }
        } finally {
            navigate('/');
            setLoading(false);
        }
    }

    // Get User
    const getUserAuth = async (tokenAuth) => {
        setLoading(true);
        try {
            if (tokenAuth) {
                const { data } = await getUser(tokenAuth);
                if (data) {
                    setAuth(data);
                    setLoading(false);
                }
                return data;
            }
        } catch (error) {
            validErrors(error);
            return undefined;
        } finally {
            setLoading(false);
        }
    }

    return {
        login,
        register,
        forgotPassword,
        logout,
        getUserAuth
    }
}

export default useAuth